/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { useMemo } from 'react';

const ChatQuickReplies = ({ quickreply, children, className, onButtonClick, config }) => {
  const quickReplies = quickreply || [];
  const shouldShowDropdown = quickReplies.length > 0;

  // Calculate dynamic width based on longest quick reply text, but respect parent container
  const dynamicWidth = useMemo(() => {
    if (quickReplies.length === 0) return 'auto';

    const maxReplyLength = Math.max(...quickReplies.map(qr => qr.title?.length || 0));
    const minWidth = 200; // Minimum width
    const maxWidth = 400; // Maximum width
    const calculatedWidth = Math.max(minWidth, Math.min(maxWidth, maxReplyLength * 8 + 40));

    // Use max-width instead of fixed width to prevent overflow
    return `max-width: ${calculatedWidth}px; width: 100%;`;
  }, [quickReplies]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        overflow-x: hidden;
        margin: 0px 1.5em 1rem;
        margin-left: 0px;
        ${dynamicWidth};
      `}
    >
      {/* Quick reply buttons */}
      {shouldShowDropdown && (
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            flex-direction: row;
            flex-shrink: 0;
            overflow-x: hidden;
            margin-top: 8px;
          `}
        >
          {children}
          {quickReplies.map((qr, index) => (
            <ChatQuickReplyButton {...qr} key={`ChatQR_${index}`} onButtonClick={onButtonClick} config={config} />
          ))}
        </div>
      )}
    </div>
  );
};

export const ChatQuickReplyButton = ({ content_type, title, payload, image_url, onButtonClick, config }) => {
  switch (content_type) {
    case 'text':
      return (
        <div
          css={theme => css`
            &:first-child {
              margin-left: 0 !important;
            }
            display: flex;
            text-align: center;
            line-height: 1.4;
            cursor: pointer;
            min-width: max-content;
            max-width: 100%;
            border-width: 1px;
            border-style: solid;
            color: ${theme.chat.agent.color};
            background-color: ${theme.chat.agent.textColor};
            border-image: initial;
            border-radius: 5px;
            margin: 0px 1em 1em 0px;
            padding: 8px 12px;
            word-wrap: break-word;
            word-break: break-word;
            overflow-wrap: break-word;
            white-space: normal;
            &:hover {
              background-color: ${theme.chat.agent.color};
              color: ${theme.chat.agent.textColor};
            }
          `}
          onClick={() =>
            onButtonClick({
              variables: {
                chatUserId: config.user.id,
                channelId: config.channel.id,
                message: title,
                payload: payload,
              },
            })
          }
        >
          {image_url && (
            <img
              css={css`
                height: 10px;
                width: 10px;
                align-self: center;
                justify-self: center;
              `}
              alt={'icon'}
              src={image_url}
            />
          )}
          {title}
        </div>
      );
    case 'location':
      return (
        <div
          css={theme => css`
            &:first-child {
              margin-left: 0 !important;
            }

            display: flex;
            color: ${theme.chat.agent.textColor};
            background-color: ${theme.chat.agent.color};
            border-radius: 5px;
            text-align: center;
            line-height: 30px;
            cursor: pointer;
            margin-left: 1rem;
            padding: 0 1rem;
            min-width: max-content;
            &:hover {
              background-color: white;
              color: ${theme.chat.agent.color};
            }
          `}
          onClick={e => {
            navigator.geolocation.getCurrentPosition(pos => {
              onButtonClick('Location', {
                location: {
                  lat: pos.coords.latitude,
                  long: pos.coords.longitude,
                },
                caption: 'Location',
              });
            });
          }}
        >
          Location
        </div>
      );
    default:
      return (
        <div
          css={theme => css`
            &:first-child {
              margin-left: 0 !important;
            }

            display: flex;
            color: ${theme.chat.agent.color};
            background-color: rgba(91, 165, 255, 0.08);
            color: ${theme.chat.agent.color};
            border-radius: 5px;
            text-align: center;
            line-height: 30px;
            cursor: pointer;
            margin-left: 1rem;
            padding: 0 1rem;
            min-width: max-content;
            &:hover {
              background-color: white;
            }
          `}
          onClick={() =>
            onButtonClick({
              variables: {
                chatUserId: config.user.id,
                channelId: config.channel.id,
                message: title,
                payload: payload,
              },
            })
          }
        >
          {title}
        </div>
      );
  }
};

export default ChatQuickReplies;
